import { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, DEFAULT_COUNTRY, findCountry } from '../lib/countries.js';

// Approximate national-number digit ranges, per country. Used to give the
// user a live "too short / too long" hint. Fallback covers everything not
// listed at 6–14 digits (E.164 national-significant-number range).
const LENGTHS = {
  GB: [10, 11], IE: [9, 10], US: [10, 10], CA: [10, 10], AU: [9, 10],
  NZ: [8, 10], IN: [10, 10], PK: [10, 10], BD: [10, 11], LK: [9, 10],
  CN: [11, 11], HK: [8, 8], SG: [8, 8], MY: [9, 10], AE: [9, 9],
  SA: [9, 9], QA: [8, 8], KW: [8, 8], BH: [8, 8], OM: [8, 8],
  NG: [10, 11], GH: [9, 9], KE: [9, 10], ZA: [9, 9], EG: [10, 10],
  DE: [10, 11], FR: [9, 9], ES: [9, 9], IT: [9, 11], NL: [9, 9],
  BE: [9, 9], PT: [9, 9], PL: [9, 9], SE: [7, 13], NO: [8, 8],
  DK: [8, 8], FI: [5, 12], CH: [9, 9], AT: [4, 13], GR: [10, 10],
  TR: [10, 10], RO: [9, 9], JP: [9, 11], KR: [9, 11], TH: [9, 9],
  PH: [10, 10], ID: [9, 12], VN: [9, 10], BR: [10, 11], MX: [10, 10],
  AR: [10, 11], IL: [9, 9], JO: [9, 9], LB: [7, 8], MA: [9, 9],
};

function validateLength(country, raw) {
  const digits = String(raw || '').replace(/[^\d]/g, '');
  if (!digits) return null; // empty — let "required" attr handle it
  const range = LENGTHS[country] || [6, 14];
  if (digits.length < range[0]) return `Too short — needs ≥${range[0]} digits for ${country}`;
  if (digits.length > range[1]) return `Too long — max ${range[1]} digits for ${country}`;
  return null;
}

export default function PhoneInput({
  value,
  onChange,
  countryCode,
  onCountryChange,
  placeholder = 'Phone number',
  required = false,
  id,
  name,
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);
  const country = findCountry(countryCode) || DEFAULT_COUNTRY;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q),
    );
  }, [filter]);

  const pick = (c) => {
    onCountryChange?.(c.code);
    setOpen(false);
    setFilter('');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'stretch',
          border: '1px solid var(--border, #d8d6e6)',
          borderRadius: 10,
          background: '#fff',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Choose country"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            border: 'none',
            borderRight: '1px solid var(--border, #d8d6e6)',
            background: '#f8f7fc',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>{country.flag}</span>
          <span style={{ color: '#5b21b6' }}>{country.dial}</span>
          <span style={{ color: '#9ca3af', fontSize: 12 }}>▾</span>
        </button>
        <input
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          required={required}
          style={{
            border: 'none',
            outline: 'none',
            padding: '10px 12px',
            fontSize: 14.5,
            background: 'transparent',
            width: '100%',
          }}
        />
      </div>

      {validateLength(country.code, value) && value && (
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
          {validateLength(country.code, value)}
        </div>
      )}

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 100,
            background: '#fff',
            border: '1px solid var(--border, #d8d6e6)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(20, 5, 60, 0.18)',
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <input
            type="text"
            placeholder="Search country or dial code"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
            style={{
              padding: '10px 12px',
              border: 'none',
              borderBottom: '1px solid var(--border, #d8d6e6)',
              outline: 'none',
              fontSize: 14,
            }}
          />
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '10px 14px', color: '#6b7280', fontSize: 13 }}>
                No matches
              </div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => pick(c)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 14px',
                  border: 'none',
                  background: country.code === c.code ? '#f3eefc' : '#fff',
                  cursor: 'pointer',
                  fontSize: 14,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#faf8ff')}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background =
                    country.code === c.code ? '#f3eefc' : '#fff')
                }
              >
                <span style={{ fontSize: 18 }}>{c.flag}</span>
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                  {c.dial}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
